import { Fragment, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Search, Truck, Layers, X, AlertTriangle } from "lucide-react";
import { api, idr } from "../../lib/api";
import InputRupiah from "../../components/InputRupiah";
import { formatTanggal, hariIniISO } from "../../lib/tanggal";
import { useModalGuard, UnsavedGuardBar } from "../../hooks/useModalGuard";
import { useAuth } from "../../context/AuthContext";
import { FormBayarUtang, RiwayatUtang, StatusPill, AGING_BADGE, labelUmur, FieldLite } from "./FinanceUtang";

/** Finance › Pelunasan Pembelian (brief owner 22 Sep 2026, keputusan H2-H4).
 *
 *  Sejak brief itu PO tidak lagi membawa cara bayar: tagihan lahir saat barang
 *  PO diterima di Inventory, dan pelunasannya pekerjaan tim Finance. Halaman ini
 *  BUKAN buku kedua — datanya `finance_utang` yang sama dengan halaman Utang,
 *  disaring DI SERVER dengan `asal=pembelian` (daftar dipotong 500 baris, jadi
 *  menyaring di layar membuat tagihan ke-501 hilang dan total di atas tabel
 *  membantah isi tabelnya, §23). Formulir bayar satu tagihan dipakai ulang dari
 *  FinanceUtang.jsx; yang baru di sini cuma "Bayar Sekaligus" untuk beberapa
 *  tagihan satu supplier (`POST /finance/utang/bayar-banyak`).
 *
 *  Semua tombol bayar memakai can("finance_utang","update") — kunci yang SAMA
 *  dengan `utang.py::pastikan_boleh_melunasi` (aturan #2/#3 CLAUDE.md). */

const TAB = [
  // "Belum Lunas" = semua yang masih aktif, bertempo atau tidak — itu yang
  // dicari staf Finance saat menyiapkan transfer, bukan pembagian lama
  // belum-jatuh-tempo vs lewat-tempo milik halaman Utang.
  { key: "aktif", label: "Belum Lunas", params: { status: "aktif" } },
  { key: "jatuh_tempo", label: "Lewat Jatuh Tempo", params: { filter: "jatuh_tempo" } },
  { key: "lunas", label: "Lunas", params: { filter: "lunas" } },
  { key: "semua", label: "Semua", params: { filter: "all" } },
];

// Cermin `utang.py::MAKS_TAGIHAN_BAYAR_BANYAK` — hanya untuk menahan tombol
// "pilih semua"; penegakannya di backend (400 kalau lebih).
const MAKS_TAGIHAN_SEKALIGUS = 50;

const sisaUtang = (u) => Math.max(0, (u.nominal || 0) - (u.nominal_terbayar || 0));

/** Pembeda supplier untuk mode pilih-banyak — cermin
 *  `utang.py::_kunci_supplier_utang` (supplier_id, jatuh ke nama huruf kecil).
 *  Di sini HANYA untuk mematikan centang baris supplier lain supaya staf tidak
 *  menyusun pilihan yang pasti ditolak; backend tetap penjaga sesungguhnya. */
function kunciSupplier(u) {
  const sid = (u.supplier_id || "").trim();
  if (sid) return `id:${sid}`;
  return "nama:" + (u.supplier_nama || "").trim().toLowerCase().split(/\s+/).join(" ");
}

export default function FinancePelunasanPembelian() {
  const { can } = useAuth();
  const bolehBayar = can("finance_utang", "update");
  // Tautan ke PO hanya untuk pemegang izin Pembelian — halaman tujuannya
  // memakai modul lain (pola yang sama dengan halaman Utang).
  const bolehLihatPO = can("inventory_po", "view");

  const [tab, setTab] = useState("aktif");
  const [cariInput, setCariInput] = useState("");
  const [cari, setCari] = useState("");
  const [supplier, setSupplier] = useState("");
  const [data, setData] = useState({ items: [], total_aktif: 0, total_jatuh_tempo: 0, suppliers: [], terpotong: false });
  const [loading, setLoading] = useState(true);
  const [galat, setGalat] = useState("");
  const [reload, setReload] = useState(0);
  const [kasBank, setKasBank] = useState([]);
  const [bayarSatu, setBayarSatu] = useState(null);
  const [bayarBanyak, setBayarBanyak] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  // Tagihan terpilih untuk Bayar Sekaligus: id -> dokumen utang. Dokumennya
  // ikut disimpan (bukan cuma id) supaya ringkasan & modal tetap benar walau
  // baris itu keluar dari daftar karena filter berubah.
  const [pilih, setPilih] = useState({});
  // Filter yang dipakai muatan SEBELUMNYA — untuk membedakan "daftar dimuat
  // ulang dengan filter yang sama" (sesudah membayar: tagihan yang hilang dari
  // daftar memang sudah lunas) dari "filter berubah" (tagihan terpilih cuma
  // tersembunyi, misalnya staf mencari nomor DO berikutnya dari supplier yang
  // sama — pilihannya TIDAK boleh ikut hilang, tinjauan 24 Sep 2026).
  const filterSebelumnya = useRef("");

  useEffect(() => {
    const t = setTimeout(() => setCari(cariInput.trim()), 350);
    return () => clearTimeout(t);
  }, [cariInput]);

  // Flag `batal`: hanya respons permintaan TERAKHIR yang boleh menyentuh state
  // (respons tab lama yang datang belakangan tidak boleh menimpa tab aktif).
  useEffect(() => {
    let batal = false;
    (async () => {
      setLoading(true);
      try {
        const params = { asal: "pembelian", ...TAB.find(t => t.key === tab).params };
        if (cari) params.cari = cari;
        if (supplier) params.supplier = supplier;
        const r = await api.get("/finance/utang", { params });
        if (batal) return;
        const items = r.data.items || [];
        setData({ ...r.data, items });
        setGalat("");
        // Pilihan yang TAMPIL di daftar diganti dokumen segar (sisa tagihannya
        // tidak basi); yang tampil tapi sudah tidak aktif dibuang. Yang TIDAK
        // tampil hanya dibuang kalau filternya sama dengan muatan sebelumnya
        // (hilang karena lunas), dipertahankan kalau filternya berubah
        // (cuma tersembunyi). Server tetap penjaga akhirnya lewat pratinjau.
        const kunciFilter = JSON.stringify(params);
        const filterSama = kunciFilter === filterSebelumnya.current;
        filterSebelumnya.current = kunciFilter;
        const tampil = Object.fromEntries(items.map(u => [u.id, u]));
        setPilih(p => Object.fromEntries(Object.entries(p)
          .filter(([id]) => (tampil[id] ? tampil[id].status === "aktif" : !filterSama))
          .map(([id, u]) => [id, tampil[id] || u])));
      } catch (e) {
        if (!batal) setGalat(e?.response?.data?.detail || "Gagal memuat tagihan pembelian");
      } finally {
        if (!batal) setLoading(false);
      }
    })();
    return () => { batal = true; };
  }, [tab, cari, supplier, reload]);

  useEffect(() => {
    let batal = false;
    api.get("/finance/accounts/kas-bank")
      .then(r => { if (!batal) setKasBank(r.data || []); })
      .catch(() => { /* formulir bayar tetap terbuka dengan pilihan kosong */ });
    return () => { batal = true; };
  }, [reload]);

  const items = data.items || [];
  const pilihan = Object.values(pilih);
  const kunciAktif = pilihan.length ? kunciSupplier(pilihan[0]) : null;
  const totalPilihan = pilihan.reduce((s, u) => s + sisaUtang(u), 0);
  const bisaDipilih = (u) => u.status === "aktif" && sisaUtang(u) > 0
    && (!kunciAktif || kunciSupplier(u) === kunciAktif);
  const togglePilih = (u) => setPilih(p => {
    const n = { ...p };
    if (n[u.id]) delete n[u.id]; else n[u.id] = u;
    return n;
  });
  const serupaTerlihat = kunciAktif ? items.filter(u => bisaDipilih(u)) : [];
  const idTampil = new Set(items.map(u => u.id));
  const pilihanTersembunyi = pilihan.filter(u => !idTampil.has(u.id)).length;
  const pilihSemuaSupplierIni = () => {
    const tambah = serupaTerlihat.slice(0, MAKS_TAGIHAN_SEKALIGUS);
    setPilih(Object.fromEntries(tambah.map(u => [u.id, u])));
    if (serupaTerlihat.length > MAKS_TAGIHAN_SEKALIGUS) {
      toast.info(`Maksimal ${MAKS_TAGIHAN_SEKALIGUS} tagihan sekali bayar — ${MAKS_TAGIHAN_SEKALIGUS} yang teratas dipilih.`);
    }
  };
  const muatUlang = () => setReload(n => n + 1);
  const kolom = bolehBayar ? 9 : 8;

  return (
    <div className="p-4 md:p-8 space-y-5" data-testid="finance-pelunasan-pembelian">
      <div>
        <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">Accounts Payable · Pembelian</p>
        <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mt-1 flex items-center gap-2">
          <Truck className="w-6 h-6 text-rose-600" /> Pelunasan Pembelian
        </h1>
        <p className="text-sm text-slate-600 mt-1 max-w-3xl">
          Tagihan supplier yang lahir saat barang PO diterima di Inventory. PO tidak lagi memilih cara bayar —
          pelunasannya di sini, satu buku dengan menu Utang. PO <strong>Barang Trade-in</strong> lunas otomatis
          dari penampung trade-in cabang dan tidak muncul sebagai tagihan.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
          <p className="text-[11px] text-slate-500 font-semibold uppercase tracking-wider">Sisa belum lunas</p>
          <p className="font-display text-xl font-bold text-rose-700 mt-1" data-testid="plb-total-aktif">{idr(data.total_aktif || 0)}</p>
          <p className="text-[11px] text-slate-400">mengikuti pencarian & supplier terpilih</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
          <p className="text-[11px] text-slate-500 font-semibold uppercase tracking-wider">Sudah lewat jatuh tempo</p>
          <p className="font-display text-xl font-bold text-amber-700 mt-1" data-testid="plb-total-jt">{idr(data.total_jatuh_tempo || 0)}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
          <p className="text-[11px] text-slate-500 font-semibold uppercase tracking-wider">Tagihan di daftar</p>
          <p className="font-display text-xl font-bold text-slate-900 mt-1" data-testid="plb-count">{items.length}</p>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-1 inline-flex flex-wrap gap-1">
        {TAB.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`plb-tab-${t.key}`}
            className={`px-4 h-9 rounded-lg text-sm font-medium transition ${tab === t.key ? "bg-slate-900 text-white shadow" : "text-slate-600 hover:bg-slate-50"}`}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={cariInput} onChange={e => setCariInput(e.target.value)} data-testid="plb-cari"
            placeholder="Cari supplier, tanggal, No PO / DO / invoice..."
            className="w-full h-10 pl-9 pr-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-rose-500" />
        </div>
        <select value={supplier} onChange={e => setSupplier(e.target.value)} data-testid="plb-supplier"
          title="Daftar supplier dihitung server dari filter tab yang aktif"
          className="h-10 px-2 rounded-lg border border-slate-200 text-sm bg-white max-w-[260px]">
          <option value="">Semua supplier</option>
          {(data.suppliers || []).map(s => <option key={s} value={s}>{s}</option>)}
          {supplier && !(data.suppliers || []).includes(supplier) && <option value={supplier}>{supplier}</option>}
        </select>
      </div>

      {data.terpotong && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800 flex items-start gap-2"
             data-testid="plb-terpotong">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          Daftar ini menampilkan {data.batas} tagihan pertama saja — persempit dengan pencarian atau pilih supplier.
          Angka total di atas tetap menghitung seluruh tagihan yang cocok.
        </div>
      )}

      {bolehBayar && pilihan.length > 0 && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 flex flex-col md:flex-row md:items-center gap-3"
             data-testid="plb-bar-pilih">
          <div className="flex-1 text-sm text-emerald-900">
            <strong>{pilihan.length}</strong> tagihan <strong>{pilihan[0].supplier_nama}</strong> dipilih ·
            total sisa <span className="font-mono font-semibold" data-testid="plb-pilih-total">{idr(totalPilihan)}</span>
            {pilihanTersembunyi > 0 && (
              <span className="block text-[11px] text-emerald-800 mt-0.5" data-testid="plb-pilih-tersembunyi">
                {pilihanTersembunyi} tagihan terpilih tidak tampil di pencarian/filter ini — tetap ikut dibayar.
              </span>
            )}
            {pilihan.length < 2 && (
              <span className="block text-[11px] text-emerald-800 mt-0.5">
                Pilih minimal 2 tagihan untuk Bayar Sekaligus — untuk satu tagihan pakai tombol Bayar di barisnya.
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {serupaTerlihat.length > pilihan.length && (
              <button type="button" onClick={pilihSemuaSupplierIni} data-testid="plb-pilih-semua-supplier"
                className="h-9 px-3 rounded-lg border border-emerald-300 bg-white text-emerald-800 text-xs font-medium hover:bg-emerald-100">
                Pilih semua tagihan supplier ini ({Math.min(serupaTerlihat.length, MAKS_TAGIHAN_SEKALIGUS)})
              </button>
            )}
            <button type="button" onClick={() => setPilih({})} data-testid="plb-batal-pilih"
              className="h-9 px-3 rounded-lg border border-slate-200 bg-white text-slate-700 text-xs font-medium hover:bg-slate-50">
              Batal pilih
            </button>
            <button type="button" onClick={() => setBayarBanyak(true)} disabled={pilihan.length < 2}
              data-testid="plb-bayar-sekaligus"
              className="h-9 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-50">
              <Layers className="w-4 h-4" /> Bayar Sekaligus
            </button>
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-100 text-[11px] uppercase tracking-wide text-slate-500">
              <tr>
                {bolehBayar && <th className="px-3 py-2.5 text-center w-10" title="Pilih untuk Bayar Sekaligus">Pilih</th>}
                <th className="px-3 py-2.5 text-left">No PO</th>
                <th className="px-3 py-2.5 text-left" title="Tanggal di bawah nomor = tanggal barang diterima (tanggal tagihan)">No DO / Invoice</th>
                <th className="px-3 py-2.5 text-left">Supplier</th>
                <th className="px-3 py-2.5 text-left">Gudang</th>
                <th className="px-3 py-2.5 text-left">Jatuh Tempo</th>
                <th className="px-3 py-2.5 text-right" title="Nilai tagihan penuh tertulis kecil di bawahnya">Sisa</th>
                <th className="px-3 py-2.5 text-center">Status</th>
                <th className="px-3 py-2.5 text-right">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {loading && items.length === 0 ? (
                <tr><td colSpan={kolom} className="p-10 text-center text-sm text-slate-400">Memuat tagihan...</td></tr>
              ) : galat ? (
                <tr><td colSpan={kolom} className="p-10 text-center text-sm text-rose-600" data-testid="plb-galat">
                  {galat}{" "}
                  <button onClick={muatUlang} className="underline" data-testid="plb-coba-lagi">Coba lagi</button>
                </td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={kolom} className="p-10 text-center text-sm text-slate-400" data-testid="plb-kosong">
                  Belum ada tagihan pembelian di filter ini
                </td></tr>
              ) : items.map(u => {
                const sisa = sisaUtang(u);
                const terpilih = !!pilih[u.id];
                const bolehCentang = terpilih || bisaDipilih(u);
                const isExpanded = expandedId === u.id;
                const riwayatCount = (u.payments || []).length + (u.cn_history || []).length;
                return (
                  <Fragment key={u.id}>
                    <tr data-testid={`plb-row-${u.id}`}
                        className={`border-b border-slate-50 hover:bg-slate-50/50 ${terpilih ? "bg-emerald-50/60" : ""}`}>
                      {bolehBayar && (
                        <td className="px-3 py-3 text-center">
                          {u.status === "aktif" && (
                            <input type="checkbox" checked={terpilih} disabled={!bolehCentang}
                              onChange={() => togglePilih(u)} data-testid={`plb-pilih-${u.id}`}
                              title={bolehCentang ? "Pilih untuk Bayar Sekaligus"
                                : "Bayar Sekaligus hanya untuk tagihan dari SATU supplier"}
                              className="w-4 h-4 rounded border-slate-300 disabled:opacity-30" />
                          )}
                        </td>
                      )}
                      <td className="px-3 py-3 whitespace-nowrap">
                        {u.po_code ? (bolehLihatPO
                          ? <Link to={`/inventory/pembelian?po=${u.source_po_id}`} data-testid={`plb-po-${u.id}`}
                                  className="font-mono text-xs text-rose-700 underline underline-offset-2">{u.po_code}</Link>
                          : <span className="font-mono text-xs text-slate-700">{u.po_code}</span>)
                          : <span className="text-xs text-slate-400">-</span>}
                        <p className="font-mono text-[10px] text-slate-400">{u.code}</p>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap">
                        <p className="font-mono text-xs text-slate-700">{u.no_invoice || "-"}</p>
                        {/* `no_invoice` = No DO supplier kalau diisi saat terima, kalau
                            tidak nomor DO internal. Tampilkan DO internal terpisah
                            supaya dua nomor itu tidak tertukar saat mencocokkan. */}
                        {u.do_code && u.do_code !== u.no_invoice && (
                          <p className="font-mono text-[10px] text-slate-400">DO {u.do_code}</p>
                        )}
                        {/* Tanggal tagihan = tanggal barang diterima; Bayar Sekaligus
                            membagi transfer ke tanggal TERTUA lebih dulu. Ditaruh di
                            sini (bukan kolom sendiri) supaya kolom Sisa & Aksi tidak
                            terdorong keluar layar laptop. */}
                        <p className="text-[10px] text-slate-500" data-testid={`plb-tanggal-${u.id}`}>
                          {u.tanggal ? formatTanggal(u.tanggal) : "-"}
                        </p>
                      </td>
                      <td className="px-3 py-3 font-medium text-slate-900">{u.supplier_nama}</td>
                      <td className="px-3 py-3 text-xs text-slate-600">{u.po_gudang || "-"}</td>
                      <td className="px-3 py-3 text-xs text-slate-700 whitespace-nowrap">
                        {u.tanggal_jatuh_tempo ? formatTanggal(u.tanggal_jatuh_tempo) : "-"}
                        {u.status === "aktif" && (
                          <span data-testid={`plb-umur-${u.id}`}
                            className={`ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold border ${AGING_BADGE[u.aging_bucket] || AGING_BADGE.no_jt}`}>
                            {labelUmur(u)}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-3 text-right whitespace-nowrap">
                        <p className="font-mono font-semibold text-slate-900" data-testid={`plb-sisa-${u.id}`}>{idr(sisa)}</p>
                        <p className="font-mono text-[10px] text-slate-400">dari {idr(u.nominal)}</p>
                      </td>
                      <td className="px-3 py-3 text-center"><StatusPill s={u.status_tampilan || u.status} /></td>
                      <td className="px-3 py-3 text-right whitespace-nowrap">
                        <button onClick={() => setExpandedId(isExpanded ? null : u.id)} data-testid={`plb-riwayat-${u.id}`}
                          className="mr-2 text-xs text-slate-500 hover:text-rose-700">
                          Riwayat{riwayatCount > 0 && ` (${riwayatCount})`}
                        </button>
                        {u.status === "aktif" && bolehBayar && (
                          <button onClick={() => setBayarSatu(u)} data-testid={`plb-bayar-${u.id}`}
                            className="text-xs font-medium text-rose-700 hover:underline">Bayar</button>
                        )}
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr data-testid={`plb-riwayat-row-${u.id}`}>
                        <td colSpan={kolom} className="px-4 py-3 bg-slate-50/60 border-b border-slate-100">
                          <RiwayatUtang payments={u.payments} cnHistory={u.cn_history} kasBank={kasBank} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {bayarSatu && (
        <FormBayarUtang utang={bayarSatu} kasBank={kasBank}
          onClose={() => { setBayarSatu(null); muatUlang(); }} />
      )}
      {bayarBanyak && (
        <FormBayarSekaligus tagihan={pilihan} kasBank={kasBank}
          onClose={() => setBayarBanyak(false)}
          onSelesai={() => { setBayarBanyak(false); setPilih({}); muatUlang(); }} />
      )}
    </div>
  );
}

/** Satu transfer untuk beberapa tagihan SATU supplier (keputusan H4). Pembagian
 *  ke tagihan tertua SELALU dari server (`/bayar-banyak/pratinjau`, aturan yang
 *  sama persis dengan eksekusinya) — tidak dihitung ulang di sini, supaya yang
 *  dilihat staf tidak pernah berbeda dari yang dijurnal. Tombol Bayar baru hidup
 *  setelah pratinjau untuk nominal + akun yang SEDANG terisi sudah tampil. */
function FormBayarSekaligus({ tagihan, kasBank, onClose, onSelesai }) {
  const totalSisa = tagihan.reduce((s, u) => s + sisaUtang(u), 0);
  const [form, setForm] = useState({
    tanggal: hariIniISO(), nominal: Math.round(totalSisa), sumber_akun_id: "", keterangan: "",
  });
  const [pratinjau, setPratinjau] = useState(null);   // { kunci, data }
  const [memuat, setMemuat] = useState(false);
  const [galat, setGalat] = useState("");
  const [busy, setBusy] = useState(false);

  // Kunci primitif (bukan larik/objek) supaya efek tidak jalan ulang tiap render.
  const idsKey = tagihan.map(u => u.id).join(",");
  const nominal = Number(form.nominal) || 0;
  const akunId = form.sumber_akun_id;
  const kunciSekarang = `${idsKey}|${nominal}|${akunId}`;

  useEffect(() => {
    if (!(nominal > 0) || !akunId) { setPratinjau(null); setGalat(""); return undefined; }
    // Pesan galat pratinjau LAMA tidak boleh tetap tampil untuk isian yang baru.
    setGalat("");
    let batal = false;
    const t = setTimeout(async () => {
      setMemuat(true);
      try {
        const r = await api.post("/finance/utang/bayar-banyak/pratinjau", {
          utang_ids: idsKey.split(","), nominal, sumber_akun_id: akunId,
        });
        if (!batal) { setPratinjau({ kunci: `${idsKey}|${nominal}|${akunId}`, data: r.data }); setGalat(""); }
      } catch (e) {
        if (!batal) { setPratinjau(null); setGalat(e?.response?.data?.detail || "Pratinjau gagal"); }
      } finally {
        if (!batal) setMemuat(false);
      }
    }, 400);
    return () => { batal = true; clearTimeout(t); };
  }, [idsKey, nominal, akunId]);

  const pratinjauCocok = !!pratinjau && pratinjau.kunci === kunciSekarang;
  const belumSiap = busy || memuat || !pratinjauCocok || !form.tanggal;

  const submit = async () => {
    if (belumSiap) return;
    setBusy(true);
    try {
      const r = await api.post("/finance/utang/bayar-banyak", {
        utang_ids: idsKey.split(","), nominal, sumber_akun_id: akunId,
        tanggal: form.tanggal, keterangan: form.keterangan,
      });
      toast.success(r.data.message);
      const sisaTak = r.data.tidak_kebagian || [];
      if (sisaTak.length) {
        toast.info(`${sisaTak.length} tagihan tidak kebagian dan tetap belum lunas: ${sisaTak.join(", ")}`,
                   { duration: 8000 });
      }
      onSelesai();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal bayar sekaligus");
    } finally { setBusy(false); }
  };
  // `dangerous` — nominal transfer & akun sumber diketik/dipilih staf sendiri.
  const guard = useModalGuard({ onClose, onSubmit: submit, busy, disabled: belumSiap, dangerous: true });
  const data = pratinjauCocok ? pratinjau.data : null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={guard.requestClose}>
      <div ref={guard.contentRef} onClick={e => e.stopPropagation()} data-testid="plb-sekaligus-modal"
           className="bg-white rounded-2xl max-w-2xl w-full p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between gap-3 mb-1">
          <h3 className="font-display font-bold text-lg flex items-center gap-2">
            <Layers className="w-5 h-5 text-emerald-600" /> Bayar Sekaligus
          </h3>
          <button onClick={guard.requestClose} className="p-1.5 hover:bg-slate-100 rounded-lg" data-testid="plb-sekaligus-tutup"><X className="w-4 h-4" /></button>
        </div>
        <p className="text-sm text-slate-600 mb-4">
          {tagihan[0]?.supplier_nama} · {tagihan.length} tagihan · total sisa {idr(totalSisa)}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FieldLite label="Tanggal Transfer">
            <input type="date" value={form.tanggal} onChange={e => setForm({ ...form, tanggal: e.target.value })}
                   className="in" data-testid="plb-sekaligus-tanggal" />
          </FieldLite>
          <FieldLite label="Nominal Transfer">
            <InputRupiah value={form.nominal} onChange={n => setForm({ ...form, nominal: n })} className="in"
                         testid="plb-sekaligus-nominal" />
          </FieldLite>
          <FieldLite label="Dari Akun" col2>
            <select value={form.sumber_akun_id} onChange={e => setForm({ ...form, sumber_akun_id: e.target.value })}
                    className="in" data-testid="plb-sekaligus-akun">
              <option value="">Pilih...</option>
              {kasBank.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </FieldLite>
          <FieldLite label="Keterangan" col2>
            <input value={form.keterangan} onChange={e => setForm({ ...form, keterangan: e.target.value })}
                   className="in" placeholder="mis. Transfer mingguan / no. referensi bank"
                   data-testid="plb-sekaligus-ket" />
          </FieldLite>
        </div>

        <div className="mt-4 border border-slate-200 rounded-lg p-3" data-testid="plb-sekaligus-pratinjau">
          <p className="text-xs font-semibold text-slate-600 mb-2">Pembagian ke tagihan (tertua dulu)</p>
          {!(nominal > 0) || !akunId ? (
            <p className="text-xs text-slate-400">Isi nominal dan pilih akun — pembagiannya tampil di sini sebelum dibayar.</p>
          ) : memuat || (!data && !galat) ? (
            <p className="text-xs text-slate-400">Menghitung pembagian...</p>
          ) : galat ? (
            <p className="text-xs text-rose-700" data-testid="plb-sekaligus-galat">{galat}</p>
          ) : (
            <>
              <table className="w-full text-xs">
                <thead className="text-[10px] uppercase text-slate-500 border-b border-slate-200">
                  <tr>
                    <th className="text-left py-1 px-1">Tagihan</th>
                    <th className="text-left py-1 px-1">Tanggal</th>
                    <th className="text-right py-1 px-1">Sisa</th>
                    <th className="text-right py-1 px-1">Dibayar</th>
                    <th className="text-center py-1 px-1">Hasil</th>
                  </tr>
                </thead>
                <tbody>
                  {data.alokasi.map(a => (
                    <tr key={a.utang_id} className="border-b border-slate-100 last:border-0"
                        data-testid={`plb-alokasi-${a.utang_id}`}>
                      <td className="py-1 px-1 font-mono">{a.code}{a.no_invoice ? ` · ${a.no_invoice}` : ""}</td>
                      <td className="py-1 px-1">{a.tanggal ? formatTanggal(a.tanggal) : "-"}</td>
                      <td className="py-1 px-1 text-right font-mono">{idr(a.sisa_sebelum)}</td>
                      <td className="py-1 px-1 text-right font-mono font-semibold">{idr(a.nominal)}</td>
                      <td className="py-1 px-1 text-center">
                        {a.lunas
                          ? <span className="text-emerald-700 font-medium">Lunas</span>
                          : <span className="text-amber-700">Sebagian</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {(data.tidak_kebagian || []).length > 0 && (
                <p className="mt-2 text-[11px] text-amber-700" data-testid="plb-sekaligus-tak-kebagian">
                  Tidak kebagian (tetap belum lunas): {data.tidak_kebagian.join(", ")}
                </p>
              )}
            </>
          )}
        </div>
        <p className="text-[11px] text-slate-500 mt-2">
          Tiap tagihan tetap mendapat jurnal & transaksinya sendiri. Kalau satu tagihan gagal dicatat,
          seluruh pembayaran ini dibatalkan — tidak ada yang tercatat setengah.
        </p>

        {guard.confirming && (
          <div className="mt-3 -mx-6"><UnsavedGuardBar onStay={guard.cancelClose} onDiscard={guard.confirmDiscard} testid="plb-sekaligus-guard" /></div>
        )}
        <div className="flex gap-2 mt-4">
          <button onClick={guard.requestClose} className="flex-1 h-10 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 text-sm" data-testid="plb-sekaligus-batal">Batal</button>
          <button onClick={submit} disabled={belumSiap} data-testid="plb-sekaligus-submit"
                  className="flex-1 h-10 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 text-sm font-medium disabled:opacity-50">
            {busy ? "Memproses..." : data ? `Bayar ${idr(data.alokasi.reduce((s, a) => s + a.nominal, 0))}` : "Bayar"}
          </button>
        </div>
        <style>{`.in { width: 100%; height: 40px; padding: 0 12px; border: 1px solid #E2E8F0; border-radius: 8px; font-size: 14px; outline: none; } .in:focus { border-color: #059669; box-shadow: 0 0 0 3px rgba(5,150,105,0.1); }`}</style>
      </div>
    </div>
  );
}
